// Google Maps Provider - loads the script once at app level
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

interface GoogleMapsContextType {
    isLoaded: boolean;
    loadError: string | null;
    google: typeof google | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
    isLoaded: false,
    loadError: null,
    google: null,
});

// Libraries to load
const GOOGLE_MAPS_LIBRARIES = ["places", "marker"];

interface GoogleMapsProviderProps {
    children: React.ReactNode;
    apiKey?: string;
}

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({
    children,
    apiKey,
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [googleInstance, setGoogleInstance] = useState<typeof google | null>(null);
    const isLoadingRef = useRef(false);
    const scriptRef = useRef<HTMLScriptElement | null>(null);

    const loadGoogleMapsScript = useCallback(() => {
        // Check if already loaded
        if (typeof window !== "undefined" && window.google?.maps) {
            setIsLoaded(true);
            setGoogleInstance(window.google);
            return;
        }

        // Check if already loading
        if (isLoadingRef.current) {
            return;
        }

        // Check for existing script
        const existingScript = document.querySelector<HTMLScriptElement>(
            'script[src*="maps.googleapis.com/maps/api/js"]'
        );

        if (existingScript) {
            // Wait for existing script to load
            const waitForLoad = () => {
                if (typeof window !== "undefined" && window.google?.maps) {
                    setIsLoaded(true);
                    setGoogleInstance(window.google);
                } else {
                    setTimeout(waitForLoad, 100);
                }
            };
            waitForLoad();
            return;
        }

        const key = apiKey || import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!key) {
            setLoadError("Google Maps API key is not configured");
            return;
        }

        isLoadingRef.current = true;

        const callbackName = `__googleMapsCallback_${Date.now()}`;

        // Define the callback
        (window as any)[callbackName] = () => {
            delete (window as any)[callbackName];
            setIsLoaded(true);
            setGoogleInstance(window.google);
            isLoadingRef.current = false;
        };

        // Create and append the script
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=${GOOGLE_MAPS_LIBRARIES.join(",")}&loading=async&callback=${callbackName}`;
        script.async = true;
        script.defer = true;

        script.onerror = () => {
            delete (window as any)[callbackName];
            setLoadError("Failed to load Google Maps script");
            isLoadingRef.current = false;
        };

        scriptRef.current = script;
        document.head.appendChild(script);
    }, [apiKey]);

    useEffect(() => {
        loadGoogleMapsScript();
    }, [loadGoogleMapsScript]);

    return (
        <GoogleMapsContext.Provider
            value={{
                isLoaded,
                loadError,
                google: googleInstance,
            }}
        >
            {children}
        </GoogleMapsContext.Provider>
    );
};

// Hook to use Google Maps context
export const useGoogleMaps = (): GoogleMapsContextType => {
    const context = useContext(GoogleMapsContext);
    if (context === undefined) {
        throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
    }
    return context;
};

export default GoogleMapsProvider;
